import React, {useContext, useEffect, useState} from "react";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {useRecoilState} from "recoil";
import {unitsAtom, unitsEnumAtom} from "../../state/rentalPeriodsRecoil";
import {ancillaryTypeAtom, chargingWayAtom, currencyAtom,} from "../../state/constantsRecoil";
import {fromGenericBeToGenericFe, fromSingleGenericBeToSingleGenericFe,} from "../../utils";
import {
    buildAncillaryExceptionBaseDefinitionValues,
    buildEntityRequestFromFormValues,
    getAncillaryExceptionBaseDefinitionValidationSchema,
    initialAncillaryExceptionBaseDefinitionValues,
} from "./BaseDefinition/AncillaryExceptionBaseDefinitionForm";
import {useLocation, useParams} from "react-router-dom";
import AncillaryExceptionBaseDefinition from "./BaseDefinition/AncillaryExceptionBaseDefinition";
import DialogComponent from "../../common/components/DialogComponent";
import {setSnackbar} from "../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import {useConstantsRepository} from "../../api/repositories/ConstantsRepository";
import {useTpUnitRepository} from "../../api/repositories/TpUnitRepository";
import {useCommercialCategoryRepository} from "../../api/repositories/CommercialCategoryRepository";
import Loading from "../../common/components/Loading";
import {Box, Grid, Tab, Tabs} from "@mui/material";
import {useTranslation} from "react-i18next";
import {DialogInterface, SelectElementModelFE, UseLocationStateModel} from "../../api/types/generic";
import {
    AncillaryExceptionBaseDefinitionFormValues,
    AncillaryExceptionEntityRequest,
    AncillaryExceptionEntityResponse,
} from "../../api/types/ancillaryException";
import {commercialCategoryListAtom} from "../../state/commercialCategoriesRecoil";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import {FormProvider, useForm} from "react-hook-form";
import {useCommercialSegmentRepository} from "../../api/repositories/CommercialSegmentRepository";
import {commercialSegmentListAtom} from "../../state/commercialSegmentsAtom";
import {CommercialCategoryEntityResponse, CommercialCategorySelectFE} from "../../api/types/commercialCategory";
import AncillaryExceptionMatrix from "./Matrix/AncillaryExceptionMatrix";
import {useAncillaryExceptionRepository} from "../../api/repositories/AncillaryExceptionRepository";
import OWRouteExceptionMatrix from "../OWAncillaryException/Matrix/OWRouteExceptionMatrix";
import {ancillaryExceptionAtom} from "../../state/ancillaryExceptionRecoil";
import {ExceptionListEntityResponse} from "../../api/types/exceptionList";
import {ancillaryServiceItem, exceptionPackAtom} from "../../state/ancillariesRecoil";
import {RouteParams} from "../../api/types/ancillary";
import {convertToCents} from "../../../../util/state/commonFunctions";
import {useAncillaryRepository} from "../../api/repositories/AncillaryRepository";


type TabPanelProps = {
    children?: React.ReactNode;
    index: number;
    value: number;
};

function TabPanel(props: TabPanelProps) {
    const {children, value, index, ...other} = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{p: 3}}>{children}</Box>
            )}
        </div>
    );
}

const AncillaryExceptionContent = ({
                                       readOnly,
                                       ancillaryExceptionId
                                   }: { readOnly: boolean, ancillaryExceptionId?: number }) => {

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    const {id} = useParams<RouteParams>();

    // Use Location
    const location = useLocation();

    const locationState: UseLocationStateModel =
        location.state as UseLocationStateModel;

    // Use Recoil
    const [, setCommercialCategoryList] = useRecoilState(commercialCategoryListAtom);
    const [, setCommercialSegmentList] = useRecoilState(commercialSegmentListAtom);
    const [, setUnits] = useRecoilState(unitsAtom);
    const [, setUnitsEnum] = useRecoilState(unitsEnumAtom);
    const [, setCurrencies] = useRecoilState(currencyAtom);
    const [, setChargingWays] = useRecoilState(chargingWayAtom);
    const [, setAncillaryTypes] = useRecoilState(ancillaryTypeAtom);
    const [ancillaryException, setAncillaryException] = useRecoilState<AncillaryExceptionEntityResponse | null>(ancillaryExceptionAtom);
    const [exceptionPack] = useRecoilState<ExceptionListEntityResponse | null>(exceptionPackAtom);
    const [serviceItem, setServiceItem] = useRecoilState<any>(ancillaryServiceItem);


    // Use State
    const [originalCommercialSegment, setOriginalCommercialSegment] = useState<SelectElementModelFE | null>(null);
    const [ancillaryExceptionBaseDefinitionValues, setAncillaryExceptionBaseDefinitionValues] = useState<AncillaryExceptionBaseDefinitionFormValues | null>(null);
    const [originalAncillaryExceptionBaseDefinitionValues, setOriginalAncillaryExceptionBaseDefinitionValues] = useState<AncillaryExceptionBaseDefinitionFormValues | null>(null);
    const [value, setValue] = useState<number>(0);
    const [dialog, setDialog] = useState<DialogInterface | null>(null);
    const [ancillaryExceptionInUse, setAncillaryExceptionInUse] = useState<boolean>(false);

    // Use Context
    const {dispatchGlobalData} = useContext(GlobalContext);

    // Use Repository Hooks
    const {
        getAncillaryException,
        getAncillaryExceptionInUse,
    } = useAncillaryExceptionRepository();
    const {getCurrencies, getChargingWays, getTypes} = useConstantsRepository();
    const {getTpUnits} = useTpUnitRepository();
    const {getCommercialCategories} = useCommercialCategoryRepository();
    const {getCommercialSegments} = useCommercialSegmentRepository();
    const {getTpUnitsEnum} = useConstantsRepository();
    const {getServiceItem} = useAncillaryRepository();

    // Use Form
    const form = useForm<AncillaryExceptionBaseDefinitionFormValues>({
        defaultValues:
            ancillaryExceptionBaseDefinitionValues ||
            initialAncillaryExceptionBaseDefinitionValues,
        resolver: yupResolver(
            getAncillaryExceptionBaseDefinitionValidationSchema(
                t,
                ancillaryException
            ),
            {},
            {
                rawValues: true,
            }
        ),
        shouldUnregister: false,
    });

    const handleSubmit = () => {
        setValue(1);
        const values: AncillaryExceptionBaseDefinitionFormValues = form.getValues();
        if(values.amountMin){
            values.amountMin =  convertToCents(values.amountMin);
        }
        if(values.amountMax){
            values.amountMax =  convertToCents(values.amountMax);
        }
        const ancillaryExceptionEntityRequest: AncillaryExceptionEntityRequest = buildEntityRequestFromFormValues(values, exceptionPack);
        // UPDATE ANCILLARY BASE
        const updateAncillaryException: AncillaryExceptionEntityResponse = {
            id: ancillaryExceptionBaseDefinitionValues?.id as number,
            ...ancillaryExceptionEntityRequest
        };
        setAncillaryException(updateAncillaryException);
    };

    const getStaticContents = async () => {
        try {
            const tpUnitsData = await getTpUnitsEnum();
            const currenciesData = await getCurrencies();
            const chargingWaysData = await getChargingWays();
            const typesData = await getTypes();

            const commercialSegmentsData = await getCommercialSegments();

            const commercialCategoriesData: CommercialCategoryEntityResponse[] =
                await getCommercialCategories();
            const commercialCategoryList: CommercialCategorySelectFE[] =
                fromGenericBeToGenericFe(commercialCategoriesData);
            setCommercialCategoryList(commercialCategoryList);

            setUnitsEnum(tpUnitsData);

            const commercialSegmentList: SelectElementModelFE[] =
                fromGenericBeToGenericFe(commercialSegmentsData);
            setCommercialSegmentList(commercialSegmentList);

            setCurrencies(currenciesData);
            setChargingWays(chargingWaysData);
            setAncillaryTypes(typesData);

            if ((locationState?.ancillaryExceptionId || id || ancillaryExceptionId) && !ancillaryExceptionBaseDefinitionValues?.id) {

                const ancillaryException: AncillaryExceptionEntityResponse = await getAncillaryException((id || locationState?.ancillaryExceptionId || ancillaryExceptionId) as number);

                const commercialCategory: CommercialCategorySelectFE = fromSingleGenericBeToSingleGenericFe(ancillaryException.commercialCategory) as CommercialCategorySelectFE;
                const commercialSegment: SelectElementModelFE = fromSingleGenericBeToSingleGenericFe(commercialCategory?.commercialSegment) as SelectElementModelFE;

                const services = await getServiceItem(ancillaryException.legalEntity, ancillaryException.dealer);
                setServiceItem(services);

                if (commercialCategory && ancillaryException && commercialSegment) {
                    const ancillaryBaseException: AncillaryExceptionBaseDefinitionFormValues =
                        buildAncillaryExceptionBaseDefinitionValues(
                            ancillaryException,
                            commercialCategory,
                            commercialSegment,
                            services,
                            t
                        );
                    setAncillaryException(ancillaryException);
                    setOriginalCommercialSegment(commercialSegment);
                    form.reset(ancillaryBaseException);
                    setAncillaryExceptionBaseDefinitionValues(
                        ancillaryBaseException
                    );
                    const originalAncillaryExceptionBaseDefinitionValues: AncillaryExceptionBaseDefinitionFormValues =
                        JSON.parse(JSON.stringify(ancillaryBaseException));

                    setOriginalAncillaryExceptionBaseDefinitionValues(
                        originalAncillaryExceptionBaseDefinitionValues
                    );
                }
            }
        } catch (e) {
        }
    };

    // Use Effect
    useEffect(() => {
        getStaticContents();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (ancillaryExceptionBaseDefinitionValues?.id) {
            reloadAncillaryDetails();
        }
        // eslint-disable-next-line
    }, [ancillaryExceptionBaseDefinitionValues?.id]);

    const reloadAncillaryDetails = async () => {
        try {
            if (ancillaryExceptionBaseDefinitionValues?.id) {
                const ancillaryExceptionInUse: boolean =
                    await getAncillaryExceptionInUse(
                        ancillaryExceptionBaseDefinitionValues.id
                    );
                setAncillaryExceptionInUse(ancillaryExceptionInUse);
            }
        } catch (e) {
            console.log(e);
        }
    };

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        // click su matrice dei prezzi ma ancillaryException non ancora creata
        if (
            newValue === 1 &&
            !ancillaryExceptionBaseDefinitionValues?.id &&
            !readOnly
        ) {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t(
                            "pricing:ancillaryException.dialogContents.createAncillaryExceptionMatrix"
                        ),
                        type: snackbarTypes.warning,
                        show: true,
                        autoClose: true,
                    },
                },
            });
        } else if (newValue === 0 && !readOnly) {
            setDialog({
                open: true,
                message: t("pricing:dialogs.messages.looseChanging"),
                onClickConfirm: () => {
                    setDialog(null);
                    setValue(newValue);
                },
            });
        }
        // click su ancillaryException
        else {
            setValue(newValue);
        }
    };

    return (
        <Grid sx={{padding: 0}}>
            <Box sx={{borderBottom: 1, borderColor: "divider"}}>
                <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                    <Tab
                        label={t("pricing:ancillaryExceptionCreate.titles.ancillaryExceptionCreate")}/>
                    <Tab
                        label={t("pricing:ancillaryExceptionCreate.titles.ancillaryExceptionMatrix")}/>
                </Tabs>
            </Box>
            <TabPanel value={value} index={0}>
                <FormProvider {...form}>
                    <AncillaryExceptionBaseDefinition
                        originalCommercialSegment={originalCommercialSegment}
                        ancillaryExceptionInUse={ancillaryExceptionInUse}
                        readOnly={readOnly}
                        handleSubmit={form.handleSubmit(handleSubmit)}
                    />
                </FormProvider>
            </TabPanel>
            <TabPanel value={value} index={1}>
                {
                    ancillaryException?.prcAncillary?.isOneWay ?
                        <OWRouteExceptionMatrix
                            owAncillaryExceptionId={
                                ancillaryExceptionId ||
                                locationState?.ancillaryExceptionId ||
                                ancillaryExceptionBaseDefinitionValues?.id ||
                                null
                            }
                            readOnly={readOnly}
                            owAncillaryExceptionInUse={ancillaryExceptionInUse}
                            setValue={setValue}
                        />
                        :
                        <AncillaryExceptionMatrix
                            ancillaryExceptionId={ancillaryExceptionId || locationState?.ancillaryExceptionId || ancillaryExceptionBaseDefinitionValues?.id || null}
                            ancillaryExceptionInUse={ancillaryExceptionInUse}
                            readOnly={readOnly}
                            setValue={setValue}
                        />
                }
            </TabPanel>
            {dialog?.open &&
                <DialogComponent
                    type={"info"}
                    onClickConfirm={dialog.onClickConfirm}
                    setOpen={() => setDialog(null)}
                    open={dialog.open}
                    message={dialog.message}
                />
            }
            <Loading/>
        </Grid>
    );
};

export default AncillaryExceptionContent;
